import * as React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Home } from '@app/Home/Home';
import { Support } from '@app/Support/Support';
import { Error } from '@app/Error/Error';
import { EntraOffboarding } from '@app/Offboarding/EntraOffboarding';
import { DefenderOffboarding } from '@app/Offboarding/DefenderOffboarding';
import { AzureOffboarding } from '@app/Offboarding/AzureOffboarding';
//import { summary } from '@app/Findings/summary';
//import { environmentFindings } from '@app/Findings/environmentFindings';
//import { assetFindings } from '@app/Findings/assetFindings';
//import { accountFindings } from '@app/Findings/accountFindings';
//import { accounts } from '@app/Findings/accounts';
//import { assets } from '@app/Findings/assets';
//import { GeneralSettings } from '@app/Settings/General/GeneralSettings';
//import { ProfileSettings } from '@app/Settings/Profile/ProfileSettings';
import { NotFound } from '@app/NotFound/NotFound';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { googleAnalytics } from '@app/utils/googleAnalytics';
import { buildTimeBranding, useSiteConfig } from '@app/config/SiteConfigContext';
import { Requests } from '@app/Requests/Requests';
import { ProtectedRoute } from '@app/utils/ProtectedRoute';
import { AuthCallback } from '@app/AuthCallback/AuthCallback';
import { AuthStrataCallback } from '@app/AuthCallback/AuthStrataCallback';
import { Unauthorized } from '@app/Unauthorized/Unauthorized';
import { Terms } from '@app/Terms/Terms';
import { CheckStatus } from '@app/CheckStatus/CheckStatus';
import { StrataLogin } from '@app/Strata/StrataLogin';

export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  component: React.ComponentType;
  path: string;
  title: string;
  routes?: undefined;
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

const buildRoutes = (siteId: string, productName: string): AppRouteConfig[] => [
  {
    component: Home,
    label: 'Home',
    path: '/',
    title: `${siteId} ${productName} | Home`,
  },
  {
    component: Support,
    label: 'How it works',
    path: '/docs',
    title: `${siteId} ${productName} | Docs`,
  },
  {
    component: Terms,
    path: '/terms',
    title: `${siteId} ${productName} | Terms and Conditions`,
  },
  {
    component: EntraOffboarding,
    path: '/offboarding/entra',
    title: `${siteId} ${productName} | Entra Offboarding`,
  },
  {
    component: DefenderOffboarding,
    path: '/offboarding/defender',
    title: `${siteId} ${productName} | Defender Offboarding`,
  },
  {
    component: AzureOffboarding,
    path: '/offboarding/azure',
    title: `${siteId} ${productName} | Azure Offboarding`,
  },
  {
    component: Error,
    path: '/error',
    title: `${siteId} ${productName} | ERROR`,
  },
  {
    component: Unauthorized,
    path: '/unauthorized',
    title: `${siteId} ${productName} | Access Denied`,
  },
  {
    component: CheckStatus,
    path: '/status',
    title: `${siteId} ${productName} | Status`,
  },
  {
    component: Requests,
    label: 'Requests',
    path: '/requests',
    title: `${siteId} ${productName} | Requests`,
  },
  {
    component: AuthCallback,
    path: '/auth/callback',
    title: `${siteId} ${productName} | Authentication`,
  },
  {
    component: AuthStrataCallback,
    path: '/auth/strata/callback',
    title: `${siteId} ${productName} | Strata authentication`,
  },
  {
    component: StrataLogin,
    path: '/strata',
    title: `${siteId} ${productName} | Strata`,
  },
];

const RouteWithTitleUpdates = ({ component: Component, title, path }: IAppRoute) => {
  useDocumentTitle(title);

  // Track page view when component mounts
  React.useEffect(() => {
    googleAnalytics.trackPageView(path, title);
  }, [path, title]);

  return <Component />;
};

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title);
  
  // Track 404 page view
  React.useEffect(() => {
    googleAnalytics.trackPageView('/404', title);
  }, [title]);
  
  return <NotFound />;
};

const AppRoutes = (): React.ReactElement => {
  const site = useSiteConfig();
  const routes = buildRoutes(
    site?.siteId || buildTimeBranding.siteId || 'QuickCheck',
    site?.productName || buildTimeBranding.applicationName || '',
  );
  const flattenedRoutes: IAppRoute[] = routes.reduce(
    (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
    [] as IAppRoute[],
  );

  return (
  <Routes>
    {flattenedRoutes.map(({ path, component, title }, idx) => {
      if (path === '/requests' || path === '/status') {
        return (
          <Route
            key={idx}
            path={path}
            element={
              <ProtectedRoute component={component} />
            }
          />
        );
      }
      return (
        <Route
          key={idx}
          path={path}
          element={
            <RouteWithTitleUpdates
              path={path}
              component={component}
              title={title}
            />
          }
        />
      );
    })}
    <Route path="*" element={<PageNotFound title="404 Page Not Found" />} />
  </Routes>
  );
};

export { AppRoutes, buildRoutes as routes };
